import PropTypes from 'prop-types';
import React, { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { Formik } from 'formik';
import { RepliconDialog } from '~/modules/common/components';
import { PERIOD_SCALE_ENUM } from '~/modules/resourcing/common/enums';
import ShowSmartFitDialogOrLoading2 from '~/modules/resourcing/components/ResourceAssignmentDialog/components/ResourceUsersChartPanel/components/ShowSmartFitDialogOrLoading/ShowSmartFitDialogOrLoading2';
import { useAllocateResourceDialogSettings } from '~/modules/resourcing/components/AllocateResourcePopup/hooks/useAllocateResourceDialogSettings';
import SetResourceAllocationDialogTitle from './SetResourceAllocationDialogTitle';
import SetResourceAllocationDialogContent from './SetResourceAllocationDialogContent';
import SetResourceAllocationDialogFooter from './SetResourceAllocationDialogFooter';
import SetResourceAllocationDialogLoadingState from './SetResourceAllocationDialogLoadingState';
import { useSetResourceAllocationFormState } from './hooks/useSetResourceAllocationFormState';
import { useSetResourceAllocationOnSubmit } from './hooks/useSetResourceAllocationOnSubmit';
import { INPUT_FIELDS_MAP } from './hooks/useSetResourceAllocationOnChangeHandlers';

const SetResourceAllocationDialogTitleId =
  'set-resource-allocation-dialog-title';

const SetResourceAllocationDialog = ({
  existingResourceAllocation,
  isSaving,
  onClose,
  onUpdateResourceAllocation,
  open,
  project,
  scale,
  user,
  defaultStartDate,
  defaultEndDate
}) => {
  const {
    isLoading: isKeyValueSettingsLoading,
    dialogSettings
  } = useAllocateResourceDialogSettings();

  const {
    initialValues,
    validationSchema,
    totalScheduledHours: queryScheduledHours,
    isScheduleLoading
  } = useSetResourceAllocationFormState({
    defaultStartDate,
    defaultEndDate,
    project,
    scale,
    userUri: user?.id,
    savedSelectedAllocationPeriod: dialogSettings?.selectedAllocationPeriod,
    savedDisplayUnit: dialogSettings?.displayUnit
  });

  const [overriddenScheduledHours, setOverriddenScheduledHours] = useState(
    null
  );
  const totalScheduledHours = overriddenScheduledHours ?? queryScheduledHours;

  const [isFormDirty, setIsFormDirty] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [focusField, setFocusField] = useState(
    INPUT_FIELDS_MAP.STARTDATE_FIELD
  );

  const {
    onFormSubmit,
    handleSetAllocation,
    isSmartFitOpen,
    smartFitSubmission,
    handleCloseSmartFitDialog
  } = useSetResourceAllocationOnSubmit({
    existingResourceAllocation,
    userUri: user?.id,
    onClose,
    onUpdateResourceAllocation,
    setFocusField
  });
  const { formatMessage } = useIntl();
  const setAllocationLabel = formatMessage({
    id: 'resourceOverAllocationConfirmationDialog.setAllocation'
  });

  const checkIsDirty = useCallback(() => isFormDirty, [isFormDirty]);

  return (
    <>
      <RepliconDialog
        aria-labelledby={SetResourceAllocationDialogTitleId}
        checkIsDirty={checkIsDirty}
        data-qe-id="SetResourceAllocationDialog"
        enableUnsavedChangesWarning
        fullWidth
        maxWidth="sm"
        open={open}
        onClose={onClose}
        trapFocusWithDatePicker
      >
        <SetResourceAllocationDialogTitle
          project={project}
          titleId={SetResourceAllocationDialogTitleId}
          user={user}
        />
        {isKeyValueSettingsLoading || isScheduleLoading ? (
          <SetResourceAllocationDialogLoadingState onClose={onClose} />
        ) : (
          <Formik
            initialValues={initialValues}
            onSubmit={onFormSubmit}
            validationSchema={validationSchema}
          >
            {isLoading ? (
              <SetResourceAllocationDialogLoadingState onClose={onClose} />
            ) : (
              <>
                <SetResourceAllocationDialogContent
                  setIsFormDirty={setIsFormDirty}
                  setIsLoading={setIsLoading}
                  totalScheduledHours={totalScheduledHours}
                  setTotalScheduledHours={setOverriddenScheduledHours}
                  user={user}
                  focusField={focusField}
                  setFocusField={setFocusField}
                  savedShowAdvancedSettings={
                    dialogSettings?.showAdvancedSettings
                  }
                />
                <SetResourceAllocationDialogFooter
                  focusField={focusField}
                  isSmartFitOpen={isSmartFitOpen}
                  isSaving={isSaving}
                  onClose={onClose}
                />
              </>
            )}
          </Formik>
        )}
      </RepliconDialog>
      {isSmartFitOpen && (
        <ShowSmartFitDialogOrLoading2
          confirmButtonLabel={setAllocationLabel}
          periodType={smartFitSubmission.periodType}
          resourceUser={user}
          scale={scale}
          totalAllocatedHours={smartFitSubmission.totalAllocatedHours}
          allocationDateRange={smartFitSubmission.allocationDateRange}
          projectUri={project?.id}
          createAllocationWithUserAndOverrideRules={handleSetAllocation}
          closeShowSmartFitDialogOrLoading={handleCloseSmartFitDialog}
        />
      )}
    </>
  );
};

SetResourceAllocationDialog.propTypes = {
  existingResourceAllocation: PropTypes.object,
  isSaving: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onUpdateResourceAllocation: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  project: PropTypes.object,
  scale: PropTypes.oneOf(Object.values(PERIOD_SCALE_ENUM)).isRequired,
  user: PropTypes.object,
  defaultStartDate: PropTypes.object,
  defaultEndDate: PropTypes.object
};

export default SetResourceAllocationDialog;
